import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  BellIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

// Global state to prevent duplicate API calls across multiple instances
const globalNotificationState = {
  isLoading: false,
  lastFetchUserId: null as string | null,
  notifications: [] as any[],
  unreadCount: 0,
  subscribers: new Set<Function>(),
  hasInitialized: false,
  
  subscribe(callback: Function): () => void {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  },
  
  notify() {
    this.subscribers.forEach(callback => callback());
  },
  
  async fetchData(userId: string, isAdmin: boolean) {
    const currentKey = `${userId}_${isAdmin}`;
    
    if (this.isLoading) {
      console.log('Global: Request already in progress, skipping');
      return;
    }
    
    if (this.lastFetchUserId === currentKey && this.hasInitialized) {
      console.log('Global: Data already fetched for this user/admin combo, skipping');
      return;
    }
    
    console.log('Global: Starting fetch for', currentKey);
    this.isLoading = true;
    this.lastFetchUserId = currentKey;
    
    try {
      const token = localStorage.getItem('authToken');
      const baseURL = isAdmin 
        ? `${process.env.REACT_APP_API_URL}/admin/notifications`
        : `${process.env.REACT_APP_API_URL}/notifications`;
      
      console.log('Global: Making notification requests to', baseURL);
      
      const [notificationsRes, unreadRes] = await Promise.all([
        axios.get(baseURL, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${process.env.REACT_APP_API_URL}/notifications/unread-count`, { 
          headers: { Authorization: `Bearer ${token}` } 
        })
      ]);
      
      console.log('Notifications response:', notificationsRes.data);
      console.log('Unread response:', unreadRes.data);
      
      if (notificationsRes.data.success) {
        this.notifications = notificationsRes.data.data.notifications || [];
      } else {
        console.error('Notifications request failed:', notificationsRes.data);
        this.notifications = [];
      }
      
      if (unreadRes.data.success) {
        this.unreadCount = unreadRes.data.data.count;
      } else {
        console.error('Unread count request failed:', unreadRes.data);
        this.unreadCount = 0;
      }
      
      this.hasInitialized = true;
      console.log('Global: Fetch completed successfully');
      this.notify();
    } catch (error) {
      console.error('Global notification fetch error:', error);
      // Reset on error so it can be retried
      this.lastFetchUserId = null;
      this.hasInitialized = false;
    } finally {
      this.isLoading = false;
    }
  }
};

interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'system';
  title: string;
  message: string;
  isRead: boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  actionUrl?: string;
  actionText?: string;
  createdAt: Date;
  data?: {
    viewerId?: string;
    viewerType?: string;
    companyName?: string;
    source?: string;
    employerId?: string;
    [key: string]: any;
  };
}

interface NotificationDropdownProps {
  isAdmin?: boolean;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ isAdmin = false }) => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>(globalNotificationState.notifications);
  const [unreadCount, setUnreadCount] = useState(globalNotificationState.unreadCount);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Subscribe to global state updates
  useEffect(() => {
    const unsubscribe = globalNotificationState.subscribe(() => {
      setNotifications([...globalNotificationState.notifications]);
      setUnreadCount(globalNotificationState.unreadCount);
    });
    
    // Immediately sync with current global state
    setNotifications([...globalNotificationState.notifications]);
    setUnreadCount(globalNotificationState.unreadCount);
    
    return unsubscribe;
  }, []);

  // Fetch data when user changes (only once per user/admin combo)
  useEffect(() => {
    if (currentUser?.uid) {
      const currentKey = `${currentUser.uid}_${Boolean(isAdmin)}`;
      
      // Only fetch if we haven't already fetched for this user/admin combination
      if (globalNotificationState.lastFetchUserId !== currentKey || !globalNotificationState.hasInitialized) {
        console.log('NotificationDropdown: Requesting data fetch for user:', currentUser.uid);
        globalNotificationState.fetchData(currentUser.uid, Boolean(isAdmin));
      } else {
        console.log('NotificationDropdown: Data already available for user:', currentUser.uid);
        // Update local state with existing global data
        setNotifications([...globalNotificationState.notifications]);
        setUnreadCount(globalNotificationState.unreadCount);
      }
    }
  }, [currentUser?.uid, isAdmin]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const markAsRead = async (notificationId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update global state
      globalNotificationState.notifications = globalNotificationState.notifications.map(notif => 
        notif.id === notificationId ? { ...notif, isRead: true } : notif
      );
      globalNotificationState.unreadCount = Math.max(0, globalNotificationState.unreadCount - 1);
      globalNotificationState.notify();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/notifications/read-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update global state
      globalNotificationState.notifications = globalNotificationState.notifications.map(notif => ({ ...notif, isRead: true }));
      globalNotificationState.unreadCount = 0;
      globalNotificationState.notify();
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      toast.error('Failed to mark all notifications as read');
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${process.env.REACT_APP_API_URL}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update global state
      const notification = globalNotificationState.notifications.find(n => n.id === notificationId);
      globalNotificationState.notifications = globalNotificationState.notifications.filter(notif => notif.id !== notificationId);
      if (notification && !notification.isRead) {
        globalNotificationState.unreadCount = Math.max(0, globalNotificationState.unreadCount - 1);
      }
      globalNotificationState.notify();
      toast.success('Notification deleted');
    } catch (error) {
      console.error('Failed to delete notification:', error);
      toast.error('Failed to delete notification');
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'error':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'system':
        return <InformationCircleIcon className="h-5 w-5 text-blue-500" />;
      default:
        return <InformationCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'border-l-red-500 bg-red-50';
      case 'high':
        return 'border-l-orange-500 bg-orange-50';
      case 'medium':
        return 'border-l-blue-500 bg-blue-50';
      default:
        return 'border-l-gray-500 bg-gray-50';
    }
  };

  const formatTimeAgo = (date: Date | any) => {
    const now = new Date();
    let createdDate: Date;
    
    // Handle different date formats
    if (date && typeof date === 'object' && date.toDate) {
      // Firestore Timestamp
      createdDate = date.toDate();
    } else if (date && typeof date === 'object' && date.seconds) {
      // Firestore Timestamp object with seconds
      createdDate = new Date(date.seconds * 1000 + (date.nanoseconds || 0) / 1000000);
    } else if (typeof date === 'string' || typeof date === 'number') {
      // String or number timestamp
      createdDate = new Date(date);
    } else if (date instanceof Date) {
      // Already a Date object
      createdDate = date;
    } else {
      // Invalid date
      return 'Just now';
    }
    
    // Handle invalid dates
    if (isNaN(createdDate.getTime())) {
      return 'Just now';
    }
    
    const diffInMs = now.getTime() - createdDate.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInWeeks = Math.floor(diffInDays / 7);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    if (diffInWeeks < 4) return `${diffInWeeks}w ago`;
    if (diffInMonths < 12) return `${diffInMonths}m ago`;
    return `${diffInYears}y ago`;
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }

    console.log('Notification clicked:', notification);
    console.log('Notification title:', notification.title);
    console.log('Notification message:', notification.message);
    console.log('Title match:', notification.title === 'Profile Viewed');
    console.log('Message includes employer viewed:', notification.message.includes('employer viewed'));

    // Handle profile view notifications for premium students
    if (notification.title === 'Profile Viewed' && notification.message.includes('employer viewed')) {
      console.log('✅ Notification matches profile view conditions, checking subscription...');
      try {
        // Check if user has premium access (student-plus or higher)
        const token = localStorage.getItem('authToken');
        console.log('Making subscription check request...');
        console.log('Token exists:', !!token);
        console.log('API URL:', process.env.REACT_APP_API_URL);
        console.log('Full endpoint URL:', `${process.env.REACT_APP_API_URL}/subscriptions/check-feature`);
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/subscriptions/check-feature`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ feature: 'detailedEmployerInsights' })
        });
        
        console.log('Response received');
        console.log('Response status:', response.status);
        console.log('Response ok:', response.ok);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('API request failed:', response.status, errorText);
          console.error('Response headers:', Object.fromEntries(response.headers.entries()));
          window.location.href = '/subscription?feature=employer-insights';
          return;
        }
        
        const result = await response.json();
        console.log('Subscription check result:', result);
        console.log('Has access:', result.hasAccess);
        console.log('Success:', result.success);
        console.log('Notification data:', notification.data);
        
        if (result.success && result.hasAccess) {
          // Check if notification has employer/viewer ID
          const employerId = notification.data?.employerId || notification.data?.viewerId;
          console.log('✅ Access granted! Employer ID found:', employerId);
          
          if (employerId) {
            console.log('✅ Redirecting to employer view:', employerId);
            window.location.href = `/employer-view/${employerId}`;
            return;
          } else {
            // If no specific employer ID, but user has access, show a generic employer insights page or message
            console.log('✅ Access granted but no specific employer ID. Notification data:', notification);
            console.log('Consider implementing a generic employer insights page or extracting employer info differently');
            // For now, still redirect to subscription page but this should be improved
            // TODO: Implement a better fallback for profile view notifications without employer IDs
            window.location.href = '/subscription?feature=employer-insights';
            return;
          }
        } else {
          console.log('❌ Access denied. Result:', result);
          console.log('- success:', result.success);
          console.log('- hasAccess:', result.hasAccess);
          // Redirect to subscription page for non-premium users
          window.location.href = '/subscription?feature=employer-insights';
          return;
        }
      } catch (error) {
        console.error('Error checking premium status:', error);
        // Fall back to subscription page
        window.location.href = '/subscription?feature=employer-insights';
        return;
      }
    }

    // Handle other action URLs
    if (notification.actionUrl) {
      window.location.href = notification.actionUrl;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-md"
      >
        <BellIcon className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-indigo-600 hover:text-indigo-800"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {!globalNotificationState.hasInitialized ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500">No notifications</div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`px-4 py-3 border-l-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                    getPriorityColor(notification.priority)
                  } ${!notification.isRead ? 'bg-blue-50' : 'bg-white'}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      {getNotificationIcon(notification.type)}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notification.isRead ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                          {notification.title}
                        </p>
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500">
                            {formatTimeAgo(notification.createdAt)}
                          </span>
                          {notification.actionText && (
                            <span className="text-xs text-indigo-600 font-medium">
                              {notification.actionText}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="ml-2 flex items-center space-x-1">
                      {!notification.isRead && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notification.id);
                          }}
                          className="p-1 text-gray-400 hover:text-green-600"
                          title="Mark as read"
                        >
                          <CheckIcon className="h-3 w-3" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification.id);
                        }}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Delete"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setIsOpen(false);
                  // Navigate to full notifications page if it exists
                }}
                className="text-xs text-indigo-600 hover:text-indigo-800 w-full text-center"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;